import React, { useEffect, useState } from 'react';
import AppShell from '../components/AppShell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Wallet, ArrowDownLeft, ArrowUpRight, Gift, CreditCard, QrCode, Loader2, TrendingUp, Copy, Check } from 'lucide-react';
import { walletSaldo, walletExtrato, recargaCheckout, recargaPix, recargaStatus } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { readPending } from '../lib/pendingEnvio';
import { useToast } from '../hooks/use-toast';

const presets = [20, 50, 100, 200, 500];

const categoriaLabel = {
  bonus: { label: 'Bônus', icon: Gift, color: 'text-emerald-600 bg-emerald-50' },
  recarga: { label: 'Recarga', icon: ArrowDownLeft, color: 'text-[#2A5BC7] bg-[#EAF1FF]' },
  envio: { label: 'Envio', icon: ArrowUpRight, color: 'text-[#F77820] bg-[#FFF1E5]' },
};

const WalletPage = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { toast } = useToast();
  const [saldo, setSaldo] = useState(0);
  const [extrato, setExtrato] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [valor, setValor] = useState('50');
  const [processing, setProcessing] = useState(false);
  const [processingValor, setProcessingValor] = useState(null);
  const [valorCustom, setValorCustom] = useState('');
  const [pix, setPix] = useState(null);
  const [copiado, setCopiado] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([walletSaldo(), walletExtrato()]);
      setSaldo(s.saldo);
      setExtrato(e);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const irParaPagamento = async (v) => {
    try {
      const { url } = await recargaCheckout(v);
      window.location.href = url;
    } catch (err) {
      toast({ title: 'Não foi possível iniciar o pagamento', description: err?.response?.data?.detail || 'Tente novamente.' });
      throw err;
    }
  };

  // Caminho rápido: clicar num valor já inicia o Pix (QR code na tela ou, se o gateway
  // escolhido nas Configurações exigir, redireciona pro checkout).
  const handlePixRapido = async (v) => {
    setProcessingValor(v);
    try {
      const r = await recargaPix(v);
      if (r.url) {
        window.location.href = r.url;
      } else {
        setPix({ recargaId: r.recarga_id, qrCode: r.qr_code, qrCodeUrl: r.qr_code_url, valor: v });
      }
    } catch (err) {
      toast({ title: 'Não foi possível iniciar o pagamento', description: err?.response?.data?.detail || 'Tente novamente.' });
    } finally {
      setProcessingValor(null);
    }
  };

  const handlePixCustom = async () => {
    const v = parseFloat(String(valorCustom).replace(',', '.'));
    if (!v || v < 10) {
      toast({ title: 'Valor inválido', description: 'O valor mínimo de recarga é R$ 10,00.' });
      return;
    }
    await handlePixRapido(v);
  };

  const handleRecarga = async () => {
    const v = parseFloat(String(valor).replace(',', '.'));
    if (!v || v < 10) {
      toast({ title: 'Valor inválido', description: 'O valor mínimo de recarga é R$ 10,00.' });
      return;
    }
    setProcessing(true);
    try {
      await irParaPagamento(v);
    } catch { setProcessing(false); }
  };

  // Volta do InfinitePay: confirma o pagamento no servidor (com algumas tentativas, pois a confirmação pode levar segundos).
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const id = q.get('recarga');
    if (!id) return;
    const params = { transaction_nsu: q.get('transaction_nsu') || undefined, slug: q.get('slug') || undefined };
    window.history.replaceState({}, '', '/carteira');
    let vivo = true;
    (async () => {
      for (let i = 0; i < 6 && vivo; i += 1) {
        try {
          const r = await recargaStatus(id, i === 0 ? params : params);
          if (r.status === 'pago') {
            toast({ title: 'Pagamento confirmado!', description: `R$ ${Number(r.valor).toFixed(2).replace('.', ',')} adicionados à sua carteira.` });
            await refreshUser();
            await load();
            return;
          }
        } catch { /* tenta de novo */ }
        await new Promise((res) => setTimeout(res, 3000));
      }
      if (vivo) toast({ title: 'Pagamento em análise', description: 'Assim que for confirmado, o saldo entra automaticamente na sua carteira.' });
    })();
    return () => { vivo = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Modal de Pix aberto (Pagar.me): fica perguntando ao servidor se já foi pago.
  useEffect(() => {
    if (!pix) return undefined;
    let vivo = true;
    (async () => {
      while (vivo) {
        try {
          const r = await recargaStatus(pix.recargaId);
          if (r.status === 'pago') {
            toast({ title: 'Pagamento confirmado!', description: `R$ ${Number(r.valor).toFixed(2).replace('.', ',')} adicionados à sua carteira.` });
            await refreshUser();
            await load();
            setPix(null);
            return;
          }
        } catch { /* tenta de novo */ }
        await new Promise((res) => setTimeout(res, 3000));
      }
    })();
    return () => { vivo = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pix?.recargaId]);

  const copiarCodigoPix = async () => {
    try {
      await navigator.clipboard.writeText(pix.qrCode);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      toast({ title: 'Não foi possível copiar', description: 'Selecione o código manualmente.' });
    }
  };

  const totais = extrato.reduce((acc, tx) => {
    if (tx.tipo === 'credito') acc.creditos += Number(tx.valor) || 0;
    else acc.debitos += Number(tx.valor) || 0;
    return acc;
  }, { creditos: 0, debitos: 0 });

  return (
    <AppShell title="Minha Carteira" subtitle="Gerencie seu saldo e veja o histórico de movimentações">
      {readPending()?.servico && (
        <div className="mb-5 bg-[#EAF1FF] border border-[#2A5BC7]/20 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-slate-700"><strong>Você tem um envio em andamento.</strong> Depois de adicionar saldo, continue de onde parou.</p>
          <Button onClick={() => navigate('/calcular')} className="inn-btn-primary rounded-full h-10 px-5">Continuar meu envio</Button>
        </div>
      )}
      {/* Saldo principal */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1E429F] via-[#2A5BC7] to-[#3970E0] text-white p-7">
          <div className="absolute -top-16 -right-16 w-64 h-64 bg-[#F77820]/30 rounded-full blur-3xl" />
          <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <Wallet className="w-5 h-5" />
              <span className="text-sm font-semibold uppercase tracking-wider text-white/85">Saldo disponível</span>
            </div>
            <p className="text-5xl font-extrabold leading-none">
              R$ {saldo.toFixed(2).replace('.', ',')}
            </p>
            <p className="mt-2 text-white/75 text-sm">Use para emitir fretes com desconto</p>

            <div className="mt-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-white/70 mb-2 flex items-center gap-1.5">
                <QrCode className="w-3.5 h-3.5" /> Adicionar saldo via Pix (crédito na hora)
              </p>
              <div className="flex flex-wrap gap-2">
                {presets.map((v) => (
                  <button
                    key={v}
                    type="button"
                    disabled={processingValor !== null}
                    onClick={() => handlePixRapido(v)}
                    className="h-10 px-4 rounded-full bg-white text-[#2A5BC7] font-bold text-sm hover:bg-white/90 disabled:opacity-60 transition-colors inline-flex items-center gap-1.5"
                  >
                    {processingValor === v ? <Loader2 className="w-4 h-4 animate-spin" /> : `R$ ${v}`}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-3">
                <input
                  type="number" min="10" step="5" inputMode="decimal" placeholder="Outro valor"
                  value={valorCustom}
                  onChange={(e) => setValorCustom(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handlePixCustom(); }}
                  className="h-10 px-3.5 w-32 rounded-full bg-white text-slate-900 placeholder-slate-400 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-white/70"
                />
                <button
                  type="button"
                  disabled={processingValor !== null}
                  onClick={handlePixCustom}
                  className="h-10 px-4 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 text-white font-semibold text-sm disabled:opacity-60 transition-colors inline-flex items-center gap-1.5"
                >
                  {processingValor !== null && processingValor === parseFloat(String(valorCustom).replace(',', '.'))
                    ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Pagar com Pix'}
                </button>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
              <button type="button" onClick={() => setOpenDialog(true)} className="text-sm text-white/80 hover:text-white underline underline-offset-2 inline-flex items-center gap-1.5">
                <CreditCard className="w-3.5 h-3.5" /> Pagar com cartão de crédito
              </button>
              <button type="button" onClick={() => window.location.href='/calcular'} className="text-sm text-white/80 hover:text-white underline underline-offset-2">
                Calcular frete
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-1 gap-4">
          <div className="bg-white rounded-2xl p-5 border border-slate-100">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
              <ArrowDownLeft className="w-4 h-4 text-emerald-600" /> Entradas
            </div>
            <p className="text-2xl font-extrabold text-slate-900">R$ {totais.creditos.toFixed(2).replace('.', ',')}</p>
            <p className="text-xs text-slate-500 mt-1">Total recarregado + bônus</p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-slate-100">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
              <ArrowUpRight className="w-4 h-4 text-[#F77820]" /> Saídas
            </div>
            <p className="text-2xl font-extrabold text-slate-900">R$ {totais.debitos.toFixed(2).replace('.', ',')}</p>
            <p className="text-xs text-slate-500 mt-1">Total gasto em envios</p>
          </div>
        </div>
      </div>

      {/* Extrato */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-lg text-slate-900">Extrato</h2>
            <p className="text-sm text-slate-500">Histórico completo de movimentações</p>
          </div>
          <TrendingUp className="w-5 h-5 text-slate-400" />
        </div>

        {loading ? (
          <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : extrato.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <Wallet className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p>Nenhuma movimentação ainda</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {extrato.map((tx) => {
              const cat = categoriaLabel[tx.categoria] || { label: tx.categoria, icon: ArrowUpRight, color: 'text-slate-600 bg-slate-100' };
              const Icon = cat.icon;
              const isCred = tx.tipo === 'credito';
              return (
                <li key={tx.id} className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${cat.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 text-sm truncate">{tx.descricao}</p>
                    <p className="text-xs text-slate-500">
                      {cat.label} · {new Date(tx.created_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold ${isCred ? 'text-emerald-600' : 'text-[#F77820]'}`}>
                      {isCred ? '+' : '−'} R$ {Number(tx.valor).toFixed(2).replace('.', ',')}
                    </p>
                    <p className="text-xs text-slate-400">Saldo: R$ {Number(tx.saldo_apos).toFixed(2).replace('.', ',')}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Dialog Recarga */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Pagar com cartão de crédito</DialogTitle>
            <DialogDescription>Escolha o valor da recarga (mínimo R$ 10,00). Prefere Pix? Feche esta janela e use os botões rápidos na tela anterior.</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Valor (R$)</label>
              <Input type="number" min="10" step="5" value={valor} onChange={(e)=>setValor(e.target.value)} className="h-11 mt-1.5 text-lg font-bold" />
              <div className="flex flex-wrap gap-2 mt-3">
                {presets.map((v) => (
                  <button key={v} type="button" onClick={()=>setValor(String(v))} className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${String(valor)===String(v) ? 'bg-[#2A5BC7] text-white border-[#2A5BC7]' : 'border-slate-200 text-slate-700 hover:border-[#2A5BC7]'}`}>
                    R$ {v}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-start gap-3 bg-[#EAF1FF] rounded-xl p-3 text-sm text-slate-700">
              <QrCode className="w-5 h-5 text-[#2A5BC7] flex-shrink-0 mt-0.5" />
              <p>Você será levado a uma <strong>página segura de pagamento</strong> para pagar com <strong>Pix</strong> ou <strong>cartão de crédito</strong>. O saldo entra assim que o pagamento é confirmado.</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpenDialog(false)}>Cancelar</Button>
            <Button onClick={handleRecarga} disabled={processing} className="inn-btn-primary rounded-full px-6">
              {processing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Abrindo pagamento...</> : 'Ir para o pagamento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal do QR code Pix (Pagar.me) */}
      <Dialog open={!!pix} onOpenChange={(v) => { if (!v) setPix(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Pagar R$ {pix ? pix.valor.toFixed(2).replace('.', ',') : ''} com Pix</DialogTitle>
            <DialogDescription>Escaneie o QR code ou copie o código no app do seu banco.</DialogDescription>
          </DialogHeader>
          {pix && (
            <div className="flex flex-col items-center gap-4 py-2">
              {pix.qrCodeUrl && (
                <img src={pix.qrCodeUrl} alt="QR code Pix" className="w-56 h-56 rounded-xl border border-slate-200" />
              )}
              <button
                type="button"
                onClick={copiarCodigoPix}
                className="w-full flex items-center gap-2 justify-center h-11 rounded-full border border-slate-200 text-sm font-semibold text-slate-700 hover:border-[#2A5BC7] transition-colors"
              >
                {copiado ? <><Check className="w-4 h-4 text-emerald-600" /> Código copiado!</> : <><Copy className="w-4 h-4" /> Copiar código Pix</>}
              </button>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Aguardando pagamento...
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
};

export default WalletPage;
